import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../hooks/useNotification';
import { useCart } from '../hooks/useCart';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ShoppingBag, ChevronRight, Trash2, ArrowLeft, Loader2 } from 'lucide-react';
import { checkoutService, CalculateResponse } from '../services/checkout.service';
import { env } from '../config/env';

const calculateShipping = (subtotal: number): number => {
  if (subtotal === 0) return 0;
  if (subtotal >= env.FREE_SHIPPING_THRESHOLD) return 0;
  return env.FLAT_SHIPPING_COST;
};

export const CartPage: React.FC = () => {
  const { showError } = useNotification();
  const { items: cartItems, subtotal, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();

  const [calculation, setCalculation] = useState<CalculateResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationFailed, setCalculationFailed] = useState(false);

  // Calculate totals using API when cart changes
  useEffect(() => {
    const calculateTotals = async () => {
      if (cartItems.length === 0) {
        setCalculation(null);
        return;
      }

      setIsCalculating(true);
      setCalculationFailed(false);
      try {
        const request = {
          items: cartItems.map(item => ({
            product_id: item.productId,
            quantity: item.quantity
          }))
        };
        const response = await checkoutService.calculate(request);
        setCalculation(response);
      } catch (error) {
        console.error('Failed to calculate totals:', error);
        setCalculationFailed(true);
        setCalculation(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateTotals();
  }, [cartItems, subtotal]);

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      showError('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white">Shopping Bag</h1>
          <p className="text-gray-500 font-medium tracking-tight">Review your selections before checkout.</p>
        </div>
        <Link to="/products" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Continue Exploring
        </Link>
      </div>

      {cartItems.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
          {/* Cart Items List */}
          <div className="lg:col-span-8 space-y-6">
            <div className="hidden border-b pb-4 md:flex text-xs font-black uppercase tracking-widest text-[#111418]/40 dark:text-white/40">
              <span className="flex-1">Product Details</span>
              <span className="w-32 text-center">Quantity</span>
              <span className="w-32 text-right">Total</span>
            </div>

            {cartItems.map(item => (
              <div key={item.id} className="flex flex-col md:flex-row items-start md:items-center gap-6 py-6 border-b border-gray-100 dark:border-gray-800 last:border-0 group">
                <div className="flex flex-1 items-center gap-6">
                  <div className="size-24 md:size-32 rounded-[1.5rem] overflow-hidden bg-gray-50 dark:bg-gray-900 border border-border/40 shadow-sm flex-shrink-0">
                    <img src={item.image} alt={item.name} className="size-full object-cover transition-transform group-hover:scale-110 duration-500" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-lg font-black text-[#111418] dark:text-white uppercase tracking-tight">{item.name}</h3>
                    <p className="text-sm font-bold text-primary">${item.price}</p>
                    <button onClick={() => removeItem(item.id)} className="text-xs font-bold text-destructive flex items-center gap-1.5 mt-2 hover:opacity-70 transition-opacity">
                      <Trash2 className="size-3.5" /> Remove item
                    </button>
                  </div>
                </div>

                <div className="flex w-full md:w-auto items-center justify-between md:justify-start gap-10">
                  <div className="flex items-center bg-gray-50 dark:bg-gray-900 rounded-xl p-1 border border-border/40 w-32 justify-between">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="size-8 flex items-center justify-center font-bold hover:bg-white dark:hover:bg-gray-800 rounded-lg">-</button>
                    <span className="text-sm font-black">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="size-8 flex items-center justify-center font-bold hover:bg-white dark:hover:bg-gray-800 rounded-lg">+</button>
                  </div>
                  <div className="text-xl font-black text-[#111418] dark:text-white w-32 text-right tracking-tight">
                    ${(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-[2rem] p-8 md:p-10 space-y-8 sticky top-24 border border-border/40">
              <h2 className="text-2xl font-black tracking-tight text-[#111418] dark:text-white">Order Summary</h2>

              {isCalculating && calculation === null ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  {calculationFailed && (
                    <p className="text-[10px] font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-widest">Estimated totals — tax calculated at checkout</p>
                  )}
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span className="text-gray-400">Subtotal</span>
                    <span className="text-[#111418] dark:text-white">${(calculation?.subtotal_amount ?? subtotal).toFixed(2)}</span>
                  </div>
                  {calculation?.tax_amount != null && calculation.tax_amount > 0 && (
                    <div className="flex justify-between items-center text-sm font-bold">
                      <span className="text-gray-400">Tax</span>
                      <span className="text-[#111418] dark:text-white">${calculation.tax_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm font-bold">
                    <span className="text-gray-400">Shipping</span>
                    <span className="text-[#111418] dark:text-white">${(calculation?.shipping_amount ?? calculateShipping(subtotal)).toFixed(2)}</span>
                  </div>
                  {calculation?.discount_amount != null && calculation.discount_amount > 0 && (
                    <div className="flex justify-between items-center text-sm font-bold text-green-600">
                      <span>Discount</span>
                      <span>-${calculation.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <Separator className="opacity-50" />
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-black text-[#111418] dark:text-white">{calculationFailed ? 'Estimated Total' : 'Total'}</span>
                    <span className="text-2xl font-black text-primary">${(calculation?.total_amount ?? (subtotal + calculateShipping(subtotal))).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4 pt-4">
                <Button className="w-full h-16 rounded-2xl text-base font-black shadow-2xl shadow-primary/20 transition-all hover:-translate-y-1 bg-primary hover:bg-primary/90 gap-2" onClick={handleCheckout}>
                  Complete Purchase <ChevronRight className="size-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-32 text-center space-y-8">
          <div className="size-24 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="size-10 text-gray-300" />
          </div>
          <div className="space-y-2">
            <h2 className="text-3xl font-black tracking-tight text-[#111418] dark:text-white">Your bag is empty</h2>
            <p className="text-gray-500 font-medium tracking-tight">Looks like you haven't added anything to your collection yet.</p>
          </div>
          <Button asChild size="lg" className="rounded-2xl h-14 px-10 font-black shadow-xl shadow-primary/10">
            <Link to="/products">Start Shopping</Link>
          </Button>
        </div>
      )}
    </div>
  );
};
